import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGame } from '../state/store'
import { LETTERS_BY_ID } from '../config/letters'
import { letterOutput } from '../engine/formulas'
import { fmt } from '../engine/decimal'
import { RARITY, UI } from '../config/colors'
import { emitToast } from './Toast'

export function Board() {
  const letters = useGame((s) => s.letters)
  const activeLetterIds = useGame((s) => s.activeLetterIds)
  const boardSlots = useGame((s) => s.boardSlots)
  const addToBoard = useGame((s) => s.addToBoard)
  const removeFromBoard = useGame((s) => s.removeFromBoard)
  const [pickerSlot, setPickerSlot] = useState<number | null>(null)

  const letterMap = new Map(letters.map((l) => [l.id, l]))
  const ownedNotOnBoard = letters.filter(
    (l) => l.level > 0 && !activeLetterIds.includes(l.id),
  )

  const handleEmptyClick = (slotIndex: number) => {
    setPickerSlot(slotIndex)
  }

  const handlePickLetter = (letterId: number) => {
    addToBoard(letterId)
    setPickerSlot(null)
    const def = LETTERS_BY_ID[letterId]
    emitToast(`Added ${def.letter} to board`, 'info')
  }

  return (
    <div className="rounded-lg border border-ink-border bg-ink-panel p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-ink-teal">
          Generator Board
        </h2>
        <span className="text-[10px] text-ink-muted">
          {activeLetterIds.length} / {boardSlots} slots
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {activeLetterIds.map((id) => {
          const state = letterMap.get(id)
          if (!state || state.level === 0) return null
          const def = LETTERS_BY_ID[id]
          const out = letterOutput(state)
          return (
            <motion.button
              key={id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                removeFromBoard(id)
                emitToast(`Removed ${def.letter} from board`, 'info')
              }}
              className="group flex items-center gap-2 rounded border border-ink-tealdim bg-ink-bg px-2 py-1.5 text-left transition hover:shadow-glow"
            >
              <span
                className="text-sm font-bold"
                style={{ color: RARITY[def.rarity] }}
              >
                [{state.capital ? def.letter : def.letter.toLowerCase()}]
              </span>
              <span className="text-[10px] text-ink-teal tabular-nums">
                {fmt(out)}/s
              </span>
              <span className="text-[10px] text-ink-muted opacity-0 group-hover:opacity-100 transition">
                ×
              </span>
            </motion.button>
          )
        })}
        {Array.from({ length: boardSlots - activeLetterIds.length }).map((_, i) => (
          <motion.button
            key={`empty-${i}`}
            whileHover={{ scale: 1.05, borderColor: UI.slotHover }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleEmptyClick(i)}
            className="flex h-9 w-20 items-center justify-center rounded border border-dashed border-ink-border text-xs text-ink-muted transition hover:border-ink-tealdim hover:text-ink-teal"
          >
            +
          </motion.button>
        ))}
      </div>

      {/* Letter picker popup — inline below slots */}
      <AnimatePresence>
        {pickerSlot !== null && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 overflow-hidden rounded-lg border border-ink-border bg-ink-bg p-3"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs text-ink-muted">Pick a letter for slot:</span>
              <button
                onClick={() => setPickerSlot(null)}
                className="text-xs text-ink-muted hover:text-ink-text"
              >
                ✕
              </button>
            </div>
            {ownedNotOnBoard.length === 0 ? (
              <div className="text-xs text-ink-muted">No available letters</div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {ownedNotOnBoard.map((ls) => {
                  const def = LETTERS_BY_ID[ls.id]
                  return (
                    <motion.button
                      key={ls.id}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handlePickLetter(ls.id)}
                      className="rounded border border-ink-tealdim bg-ink-bg px-2 py-1 text-xs text-ink-teal hover:shadow-glow"
                    >
                      [{ls.capital ? def.letter : def.letter.toLowerCase()}]
                    </motion.button>
                  )
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
